import { Link, NavLink, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { hasRole, type UserRole } from '@collector/shared'
import { useAuth } from '../lib/auth'
import { UserMenu } from './UserMenu'

/**
 * `roles` restricts a menu item to those users; omitted means everyone
 * authenticated sees it. Schemas are intentionally developer-only: regular
 * admins are not exposed to the JSON Schema registry.
 *
 * There is no "Home" nav item — the product brand link in the header is
 * the home entry point.
 */
interface NavItem {
  to: string
  labelKey: string
  roles?: UserRole[]
}

const navItems: NavItem[] = [
  { to: '/data-sets', labelKey: 'nav.dataSets' },
  { to: '/decode-label', labelKey: 'nav.decodeLabel' },
  { to: '/tablets', labelKey: 'nav.tablets', roles: ['admin', 'developer'] },
  { to: '/users', labelKey: 'nav.users', roles: ['admin', 'developer'] },
  { to: '/schemas', labelKey: 'nav.schemas', roles: ['developer'] },
]

export function AppShell() {
  const auth = useAuth()
  const { t } = useTranslation()

  return (
    <div className="min-h-screen">
      <header className="border-b border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-900">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="group flex items-center gap-3 rounded text-2xl font-semibold text-stone-900 dark:text-stone-100"
              aria-label={t('nav.homeAriaLabel')}
            >
              <img
                src="/brand-mark.png"
                alt=""
                aria-hidden="true"
                className="h-[60px] w-[60px]"
                width={60}
                height={60}
              />
              <span className="ml-2 flex flex-col whitespace-nowrap leading-tight group-hover:underline">
                <span>AO Patient</span>
                <span>Database</span>
              </span>
            </Link>
            <nav className="flex items-center gap-1">
              {navItems
                .filter((i) => !i.roles || hasRole(auth.user, ...i.roles))
                .map((i) => (
                  <NavLink
                    key={i.to}
                    to={i.to}
                    className={({ isActive }) =>
                      `whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ${
                        isActive
                          ? 'bg-emerald-800 text-white'
                          : 'text-stone-700 hover:bg-stone-100 dark:text-stone-200 dark:hover:bg-stone-800'
                      }`
                    }
                  >
                    {t(i.labelKey)}
                  </NavLink>
                ))}
            </nav>
          </div>
          <UserMenu />
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-6 py-6">
        <Outlet />
      </main>
    </div>
  )
}
